/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  Loading,
  ProgressIndicator,
  ProgressStep
} from '@carbon/react';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { useAppDispatch, useAppSelector } from '../../../store';
import { Noop, ApiStatus } from '../../../ts';
import { FeatureFlag } from '../../../ts/feature-flags';
import { SEARCH_AUTH_TYPES, SEARCH_INTEGRATION_TYPES } from '../../../ts/skill';
import { ConversationalSearch } from '../../elastic-search/components/ConversationalSearch';
import { ReadOnlyNotification } from '../../ia/components/access-control/ReadOnlyNotification';
import { EnvironmentTag } from '../../ia/components/environment-tag/EnvironmentTag';
import Panel from '../../ia/components/panel/Panel';
import { isSkillReadOnly } from '../../ia/store/access-control/selectors';
import { selectAssistant } from '../../ia/store/assistants/core-selectors';
import { updateEnvironment } from '../../ia/store/environments/actions';
import {
  selectIsSkillEnabledInEnvironment,
  selectSkillReferencesForEnvironment,
  selectIsUpdatingEnvironment
} from '../../ia/store/environments/selectors';
import {
  isFeatureEnabled,
  selectIsConversationalSearchEnabled
} from '../../ia/store/features/selectors';
import { listSkills, updateSkill } from '../../ia/store/skills/actions';
import { selectSkillById } from '../../ia/store/skills/core-selectors';
import {
  selectIsUpdatingSkill,
  selectIsCreatingSkill,
  selectAreSkillsLoading
} from '../../ia/store/skills/selectors';
import { useMilvusDraft } from '../hooks/useMilvusDraft';
import {
  checkCreds,
  getModels,
  listDataSources,
  listCollections,
  getIndices,
  getFields
} from '../store/actions';
import {
  resetState,
  clearDiscoveryDetails,
  resetIsDirty,
  setAuthTypeDefault,
  setSearchSkillDraft,
  setIsSetupValid,
  setAuthType
} from '../store/reducer';
import {
  selectDraftSearchSkill,
  selectDraftUser,
  selectDraftPass,
  selectDraftUrl,
  selectIsSetupValid,
  selectAuthType,
  selectIsCheckCredsLoading,
  selectCheckCredsStatus,
  selectDraftDatabase,
  selectDraftCollection,
  selectDraftIndex,
  selectDraftModelId,
  selectDraftTitleOptionValue,
  selectDraftBodyOptionValue,
  selectDraftPort
} from '../store/selectors';
import { messages } from './messages';
import { MilvusDataSource } from './MilvusDataSource';
import { MilvusInstanceConfig } from './MilvusInstanceConfig';
import { MilvusTabs } from './MilvusTabs';
import './MilvusOverlay.scss';

interface MilvusOverlayProps {
  environmentId: string;
  skillId: string;
  onClose: Noop;
}

const MilvusOverlay = ({
  environmentId,
  skillId,
  onClose
}: MilvusOverlayProps) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useAppDispatch();
  const draft = useMilvusDraft();
  const [progressIndex, setProgressIndex] = useState(0);
  const [showTab, setShowTab] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isInitializingDraftSkill, setIsInitializingDraftSkill] =
    useState(true);
  const assistant = useAppSelector(selectAssistant);
  const assistantId = assistant?.assistant_id;

  const isConversationalSearchEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.DISCOVERY_LLM_CONVERSATIONAL_SEARCH)
  );
  const isSapConversationalSearchEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH)
  );
  const isReadOnly = useAppSelector(state => isSkillReadOnly(state, skillId));
  const conversationalSearchToggleVisible = useAppSelector(
    selectIsConversationalSearchEnabled
  );
  const isEnabled = useAppSelector(state =>
    selectIsSkillEnabledInEnvironment(state, environmentId, skillId)
  );
  const skillReferences = useAppSelector(state =>
    selectSkillReferencesForEnvironment(state, environmentId)
  );
  const isEnvironmentUpdating = useAppSelector(selectIsUpdatingEnvironment);
  const draftSkill = useAppSelector(selectDraftSearchSkill);
  const isCheckCredsLoading = useAppSelector(selectIsCheckCredsLoading);
  const checkCredsStatus = useAppSelector(selectCheckCredsStatus);
  const isMilvusSetupValid = useAppSelector(selectIsSetupValid);
  const skill = useAppSelector(state => selectSkillById(state, skillId));
  const draftUser = useAppSelector(selectDraftUser);
  const draftPass = useAppSelector(selectDraftPass);
  const draftUrl = useAppSelector(selectDraftUrl);
  const draftPort = useAppSelector(selectDraftPort);
  const draftAuthType = useAppSelector(selectAuthType);
  const draftDB = useAppSelector(selectDraftDatabase);
  const draftCollection = useAppSelector(selectDraftCollection);
  const draftIndex = useAppSelector(selectDraftIndex);
  const draftModel = useAppSelector(selectDraftModelId);
  const draftTitle = useAppSelector(selectDraftTitleOptionValue);
  const draftBody = useAppSelector(selectDraftBodyOptionValue);
  const isUpdating = useAppSelector(selectIsUpdatingSkill);
  const isCreating = useAppSelector(selectIsCreatingSkill);
  const areSkillsLoading = useAppSelector(selectAreSkillsLoading);
  const showLoading =
    isCheckCredsLoading ||
    isEnvironmentUpdating ||
    isUpdating ||
    isCreating ||
    areSkillsLoading ||
    isInitializingDraftSkill;
  const credentials =
    draftUrl &&
    draftPort &&
    ((draftUser && draftPass) || draftAuthType === SEARCH_AUTH_TYPES.NONE);
  // dataSources is used to verify that all the required fields are selected for the initial setup.
  const dataSources =
    draftDB &&
    draftCollection &&
    draftIndex &&
    draftModel &&
    draftTitle &&
    draftBody;
  const tabsChanged = isMilvusSetupValid && draft.isDirty;
  const isSubmitButtonEnabled =
    (!isReadOnly || isCpd) &&
    ((!showTab &&
      ((progressIndex === 0 && credentials) ||
        (progressIndex === 1 && dataSources) ||
        progressIndex === 2)) ||
      (tabsChanged && dataSources));
  let submitButtonLabel;
  if (showTab) {
    submitButtonLabel = t(messages.save);
  } else if (
    progressIndex === 0 ||
    (progressIndex === 1 && conversationalSearchToggleVisible)
  ) {
    submitButtonLabel = t(messages.next);
  } else {
    submitButtonLabel = t(messages.save);
  }

  useEffect(() => {
    if (skillId && !skill) {
      dispatch(listSkills(environmentId));
    }
  }, [skillId, skill, dispatch, environmentId]);

  useEffect(
    () => {
      // If store has data on Milvus load the tabs
      if (skill?.search_settings?.milvus && isInitialLoad) {
        // Go to tab screen
        setShowTab(true);
        dispatch(setIsSetupValid(true));
        // Load store data in the draft redux state
        dispatch(setSearchSkillDraft({ skill, isDirty: false }));
        // Load the data source for the dropdowns
        dispatch(listDataSources({ skillId, assistantId }));
        dispatch(
          listCollections({
            skillId,
            assistantId,
            database: skill?.search_settings?.milvus?.database
          })
        );

        const collection = skill?.search_settings?.milvus?.collection;
        // Get indices for the collection selected
        dispatch(
          getIndices({
            skillId,
            assistantId,
            collectionName: collection
          })
        );
        dispatch(
          getFields({
            skillId,
            assistantId,
            collectionName: collection
          })
        );
        // Get models for dropdown
        dispatch(getModels());
        // Update initial load so we don't load the dropdowns again after a save
        setIsInitialLoad(false);
        // if this is the username then the user is using the watson API key
        if (skill?.search_settings?.milvus?.username === 'ibmlhapikey') {
          dispatch(setAuthType(SEARCH_AUTH_TYPES.API));
        } else {
          dispatch(setAuthTypeDefault());
        }
      } else if (skill?.search_settings && skill?.skill_id !== draft?.skillId) {
        // New Milvus search, initialize fields.
        draft.initializeAutocreatedSkill(skill);

        // Since we're starting from scratch, set the default auth type to basic and don't set a previously saved auth
        // type value.
        dispatch(setAuthTypeDefault());
      }

      // If this was previously a Discovery search skill make sure to delete those settings from the draft.
      if (skill?.search_settings?.discovery) {
        dispatch(clearDiscoveryDetails());
      }

      if (isConversationalSearchEnabled) {
        if (_.isEmpty(skill?.search_settings?.conversational_search)) {
          // If conversational search is enabled, and this is an older skill that doesn't have anything set for
          // conversational_search, make sure to set the values in draft to the defaults.
          draft.setConversationalSearchEnabledDefault();
          draft.setConversationalSearchConfidenceThresholdDefault();
          draft.setConversationalSearchMultiTurnDefault();
          if (!isSapConversationalSearchEnabled) {
            // If SAP conversational search is enabled we don't want to set a citations_title because they are using a
            // text response type instead of the new conversational_search response type. If SAP conversational search
            // is disabled then set a default for citations_title.
            draft.setCitationsTitleValueDefault();
          }
        } else if (
          !skill?.search_settings?.conversational_search?.citations_title &&
          !isSapConversationalSearchEnabled
        ) {
          // If this is a newer skill that includes conversational_search settings, but there is no value for the
          // citations title in the skill, and SAP conversational search is disabled, then make sure to set the value in
          // draft to the default.
          draft.setCitationsTitleValueDefault();
        }
      }

      // After the draft skill is done being initialized update state to close the loading spinner.
      setIsInitializingDraftSkill(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including all `draft` causes loop, need only skillId
    [
      dispatch,
      draft?.skillId,
      isConversationalSearchEnabled,
      isSapConversationalSearchEnabled,
      skill
    ]
  );
  const updateSkillAndEnvironment = useCallback(async () => {
    // Send the updated skill to store.
    await dispatch(updateSkill(draftSkill)).unwrap();

    // Important:
    // If the skill has not been enabled yet (because it was a pre-created skill and this is the first time it's
    // being setup) then send another post to set the skill_ref to enabled.
    if (!isEnabled) {
      const updatedSkillRefs = _.cloneDeep(skillReferences);
      const targetSkill = updatedSkillRefs.find(
        skillRef => skillRef.skill_id === skillId
      );
      targetSkill.disabled = false;
      await dispatch(
        updateEnvironment({
          assistant_id: assistantId,
          environment_id: environmentId,
          skill_references: updatedSkillRefs
        })
      );
    }

    // Reset progress index
    setProgressIndex(0);
    // Now that the skill has been updated, reset the isDirty property on the elastic draft.
    await dispatch(resetIsDirty());
  }, [
    dispatch,
    assistantId,
    environmentId,
    skillReferences,
    isEnabled,
    skillId,
    draftSkill
  ]);

  const handleClose = useCallback(() => {
    dispatch(resetState());
    onClose();
  }, [dispatch, onClose]);

  const handleSubmit = useCallback(async () => {
    if (progressIndex === 0 && !showTab) {
      // check credentials
      dispatch(checkCreds({ assistantId }));
    } else if (conversationalSearchToggleVisible && progressIndex === 1) {
      setProgressIndex(progressIndex + 1);
    } else if (progressIndex === 1 || progressIndex === 2 || showTab) {
      //save configuration
      await updateSkillAndEnvironment();
      setShowTab(true);
      await dispatch(resetIsDirty());
    }
  }, [
    dispatch,
    progressIndex,
    assistantId,
    conversationalSearchToggleVisible,
    updateSkillAndEnvironment
  ]);

  useEffect(() => {
    if (checkCredsStatus === ApiStatus.SUCCEEDED) {
      setProgressIndex(progressIndex + 1);
      //Call DB and collections for instance
      dispatch(listDataSources({ skillId, assistantId }));
      dispatch(getModels());
    }
  }, [checkCredsStatus]);

  // Clean up milvus state when leaving without closing the panel.
  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  //Clear out draft data and start initial config.
  const updateConfig = () => {
    draft.initializeAutocreatedSkill(skill);
    dispatch(setIsSetupValid(false));
    dispatch(resetState());
    dispatch(setAuthType(SEARCH_AUTH_TYPES.BASIC));
    setShowTab(false);
  };

  return (
    <Panel className="MilvusOverlay" title={t(messages.title)}>
      <header
        className="MilvusOverlay__header"
        aria-labelledby="milvus-panel-heading">
        <div className="MilvusOverlay__header--name">
          <h2 id="milvus-panel-heading">{t(messages.title)}</h2>
        </div>
        <div className="ChannelOverlay__header--tag">
          <EnvironmentTag environmentId={environmentId} />
        </div>
        <div className="MilvusOverlay__header--actions">
          <Button kind="secondary" onClick={handleClose} id="closePanel">
            {t(messages.close)}
          </Button>
          <Button
            kind="primary"
            onClick={handleSubmit}
            disabled={!isSubmitButtonEnabled}>
            {submitButtonLabel}
          </Button>
        </div>
      </header>
      {!isCpd && <ReadOnlyNotification readOnly={isReadOnly} />}
      <div className="MilvusOverlay__content">
        {showLoading && <Loading withOverlay />}
        {isMilvusSetupValid ? (
          <MilvusTabs
            draft={draft}
            skillId={skillId}
            assistantId={assistantId}
            readOnly={isReadOnly}
            onClose={onClose}
            updateConfig={updateConfig}
          />
        ) : (
          <>
            <div className="MilvusOverlay__progress-indicator">
              {conversationalSearchToggleVisible ? (
                <ProgressIndicator
                  currentIndex={progressIndex}
                  spaceEqually={true}>
                  <ProgressStep label={t(messages.connect)} />
                  <ProgressStep label={t(messages.index)} />
                  <ProgressStep label={t(messages.search)} />
                </ProgressIndicator>
              ) : (
                <ProgressIndicator
                  currentIndex={progressIndex}
                  spaceEqually={true}>
                  <ProgressStep label={t(messages.connect)} />
                  <ProgressStep label={t(messages.index)} />
                </ProgressIndicator>
              )}
            </div>
            {progressIndex === 0 && (
              <MilvusInstanceConfig
                readOnly={isReadOnly}
                onClose={onClose}
                noHeader={false}
                updateConfig={updateConfig}
              />
            )}
            {progressIndex === 1 && (
              <MilvusDataSource
                showTab={showTab}
                skillId={skillId}
                assistantId={assistantId}
                readOnly={isReadOnly}
              />
            )}
            {conversationalSearchToggleVisible && progressIndex === 2 && (
              <ConversationalSearch
                searchType={SEARCH_INTEGRATION_TYPES.MILVUS}
              />
            )}
          </>
        )}
      </div>
    </Panel>
  );
};

export { MilvusOverlay };
